// src/pages/ManagePromosPage.js
import React, { useState, useEffect } from 'react';
import { 
  collection, addDoc, getDocs, deleteDoc, doc, updateDoc, Timestamp, query, orderBy 
} from 'firebase/firestore';

// ✅ PERBAIKAN DI SINI: Import 'db' langsung, bukan 'app'
import { db } from '../firebaseConfig'; 

import { Plus, Trash2, Ticket, XCircle, Search, Calendar, Tag } from 'lucide-react';

export default function ManagePromosPage() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Daftar Kota/Area Operasional Zimo App
  const AVAILABLE_AREAS = [
    'Jabodetabek', 'Kota Bandung', 'Kota Semarang', 'Kota Yogyakarta',
    'Kota Surabaya', 'Kota Medan', 'Kota Palembang', 'Kota Batam',
    'Kota Bandar Lampung', 'Kota Jambi', 'Kota Solo', 'Kota Denpasar',
    'Kota Balikpapan', 'Kota Makassar', 'Kota Manado'
  ];

  // State Form
  const [formData, setFormData] = useState({
    code: '', 
    title: '',
    imageUrl: '',
    description: '',
    discountPercent: 0, 
    maxDiscount: 0, 
    minTransaction: 0,
    minDistance: 0, // ✅ TAMBAHAN: Syarat minimum jarak tempuh (KM)
    isActive: true,
    startDate: '', 
    endDate: '',
    validTimeStart: '09:00', 
    validTimeEnd: '23:59',
    validServices: [], 
    validPaymentMethods: [], 
    validDays: [],
    // ✅ TAMBAHAN FIELD BARU
    targetAreas: [], // Jika kosong = Berlaku di semua kota
    userSegment: 'all', // 'all', 'new_user', 'loyal_user', 'sleeping_user'
    usageLimitPerUser: 1, // Default batas pakai per orang
    globalQuota: 0 // 0 = Kuota unlimited
  });

  // --- 1. FETCH DATA (READ) ---
  const fetchPromos = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "promos"), orderBy("code"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startDate: doc.data().startDate?.toDate(),
        endDate: doc.data().endDate?.toDate(),
      }));
      setPromos(data);
    } catch (error) {
      console.error("Error fetching promos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromos();
  }, []);

  // --- 2. HANDLE INPUT FORM ---
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({ ...formData, [name]: type === 'number' ? Number(value) : value });
  };

  const handleArrayChange = (field, value) => {
    const current = formData[field];
    if (current.includes(value)) {
      setFormData({ ...formData, [field]: current.filter(item => item !== value) });
    } else {
      setFormData({ ...formData, [field]: [...current, value] });
    }
  };

  // --- 3. SUBMIT (CREATE) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        code: formData.code.toUpperCase(),
        title: formData.title,
        imageUrl: formData.imageUrl || '', // ✅ AMAN DARI UNDEFINED
        description: formData.description || '', // Sekalian amankan deskripsi
        discountPercent: formData.discountPercent / 100, 
        maxDiscount: Number(formData.maxDiscount),
        minTransaction: Number(formData.minTransaction),
        minDistance: Number(formData.minDistance), // ✅ SIMPAN DATA JARAK
        isActive: formData.isActive === 'true' || formData.isActive === true,
        validServices: formData.validServices,
        validPaymentMethods: formData.validPaymentMethods,
        validDays: formData.validDays.sort((a, b) => a - b),
        validTimeStart: formData.validTimeStart,
        validTimeEnd: formData.validTimeEnd,
        startDate: formData.startDate ? Timestamp.fromDate(new Date(formData.startDate)) : null,
        endDate: formData.endDate ? Timestamp.fromDate(new Date(formData.endDate)) : null,
        allowedUserIds: [], 
        usedByUserIds: [],
        // ✅ TAMBAHAN FIELD BARU
        targetAreas: formData.targetAreas,
        userSegment: formData.userSegment,
        usageLimitPerUser: Number(formData.usageLimitPerUser),
        globalQuota: Number(formData.globalQuota)
      };

      await addDoc(collection(db, "promos"), payload);
      alert("Promo berhasil dibuat!");
      setIsModalOpen(false);
      fetchPromos(); 
      
      setFormData({
        code: '', title: '', imageUrl: '', description: '', discountPercent: 0, maxDiscount: 0, // ✅ MASUKKAN imageUrl KE SINI
        minTransaction: 0, minDistance: 0, isActive: true, startDate: '', endDate: '', // ✅ RESET JARAK
        validTimeStart: '09:00', validTimeEnd: '23:59',
        validServices: [], validPaymentMethods: [], validDays: [],
        // ✅ RESET FIELD BARU
        targetAreas: [], userSegment: 'all', usageLimitPerUser: 1, globalQuota: 0
      });

    } catch (error) {
      alert("Gagal: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // --- 4. ACTIONS (DELETE & TOGGLE) ---
  const handleDelete = async (id) => {
    if (window.confirm("Yakin hapus promo ini?")) {
      try {
        await deleteDoc(doc(db, "promos", id));
        alert("Promo berhasil dihapus!");
        fetchPromos(); // Refresh data setelah berhasil dihapus
      } catch (error) {
        console.error("Error menghapus promo:", error);
        alert("Gagal menghapus promo! Cek console untuk detailnya. Error: " + error.message);
      }
    }
  };

  const toggleStatus = async (promo) => {
    await updateDoc(doc(db, "promos", promo.id), { isActive: !promo.isActive });
    fetchPromos();
  };

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Ticket className="text-green-500" /> Manajemen Kode Promo
          </h2>
          <p className="text-gray-400 text-sm">Atur voucher diskon untuk pengguna aplikasi.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition shadow-lg shadow-green-900/20"
        >
          <Plus size={18} /> Tambah Promo
        </button>
      </div>

      {/* TABLE LIST */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-xl">
        <table className="w-full text-left text-sm text-gray-300">
          <thead className="bg-gray-900 text-gray-400 uppercase font-medium border-b border-gray-700">
            <tr>
              <th className="p-4">Kode</th>
              <th className="p-4">Diskon</th>
              <th className="p-4">Layanan & Area</th>
              <th className="p-4">Periode & Kuota</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {loading ? (
              <tr><td colSpan="6" className="p-8 text-center animate-pulse">Memuat data promo...</td></tr>
            ) : promos.length === 0 ? (
              <tr><td colSpan="6" className="p-8 text-center text-gray-500">Belum ada promo yang dibuat.</td></tr>
            ) : (
              promos.map((promo) => (
                <tr key={promo.id} className="hover:bg-gray-700/50 transition">
                  <td className="p-4 font-bold text-white flex items-center gap-2">
                    <Tag size={14} className="text-blue-400"/> {promo.code}
                  </td>
                  <td className="p-4">
                    <span className="text-green-400 font-bold text-lg">{(promo.discountPercent * 100).toFixed(0)}%</span>
                    <div className="text-xs text-gray-500">Max: Rp {promo.maxDiscount.toLocaleString()}</div>
                  </td>
                  <td className="p-4">
                    {promo.validServices.length > 0 
                      ? <div className="flex flex-wrap gap-1">
                          {promo.validServices.map(s => (
                            <span key={s} className="px-2 py-0.5 bg-gray-700 rounded text-xs border border-gray-600">{s}</span>
                          ))}
                        </div>
                      : <span className="text-gray-500 italic mb-1 block">Semua Layanan</span>}
                    
                    {/* ✅ TAMBAHAN TAMPILAN AREA & JARAK */}
                    <div className="mt-2 flex flex-wrap gap-1 items-center text-[10px]">
                      <span className="font-bold text-pink-400 bg-pink-500/10 border border-pink-500/30 px-1.5 py-0.5 rounded uppercase">
                        {promo.targetAreas?.length > 0 ? promo.targetAreas.join(', ') : '🌎 NASIONAL'}
                      </span>
                      {promo.minDistance > 0 && (
                        <span className="font-bold text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 px-1.5 py-0.5 rounded">
                          MIN {promo.minDistance} KM
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-xs">
                    <div className="flex items-center gap-1 text-gray-300">
                      <Calendar size={12}/> {promo.startDate ? promo.startDate.toLocaleDateString() : '-'}
                    </div>
                    <div className="text-gray-500 pl-4 mb-2">s/d {promo.endDate ? promo.endDate.toLocaleDateString() : '-'}</div>
                    
                    {/* ✅ TAMBAHAN TAMPILAN JAM & KUOTA */}
                    <div className="text-gray-400 space-y-0.5">
                      <div>Jam: <span className="text-white font-medium">{promo.validTimeStart || '00:00'} - {promo.validTimeEnd || '23:59'}</span></div>
                      <div>Kuota: <span className="text-white font-bold">{promo.globalQuota === 0 ? '♾️ Unlimited' : promo.globalQuota}</span></div>
                    </div>
                  </td>
                  <td className="p-4">
                    <button 
                      onClick={() => toggleStatus(promo)}
                      className={`px-3 py-1 rounded-full text-xs font-bold transition ${
                        promo.isActive 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30' 
                        : 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                      }`}
                    >
                      {promo.isActive ? 'AKTIF' : 'NONAKTIF'}
                    </button>
                  </td>
                  <td className="p-4 flex justify-center gap-2">
                    <button 
                      onClick={() => handleDelete(promo.id)} 
                      className="p-2 bg-red-600/20 text-red-400 rounded hover:bg-red-600/30 hover:text-red-300 transition"
                      title="Hapus Promo"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL FORM TAMBAH */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto">
          <div className="bg-gray-900 border border-gray-700 w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col my-10 max-h-[90vh]">
            <div className="p-5 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-900 z-10 rounded-t-2xl">
              <div>
                <h3 className="text-xl font-bold text-white">Buat Kode Promo Baru</h3>
                <p className="text-xs text-gray-400">Isi detail voucher diskon di bawah ini.</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white transition">
                <XCircle size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto">
              
              {/* SECTION 1: INFO UTAMA */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-blue-400 uppercase tracking-wider">Informasi Dasar</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 uppercase font-semibold mb-1 block">Kode Promo <span className="text-red-500">*</span></label>
                    <input 
                      name="code" 
                      required 
                      onChange={handleChange} 
                      className="w-full bg-gray-800 border border-gray-700 rounded p-3 text-white uppercase font-bold focus:border-blue-500 outline-none" 
                      placeholder="MISAL: HEMAT50" 
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 uppercase font-semibold mb-1 block">Judul Promo <span className="text-red-500">*</span></label>
                    <input 
                      name="title" 
                      required 
                      onChange={handleChange} 
                      className="w-full bg-gray-800 border border-gray-700 rounded p-3 text-white focus:border-blue-500 outline-none" 
                      placeholder="Diskon Awal Tahun" 
                    />
                  </div>
                </div>

                {/* ✅ AWAL TAMBAHAN: Input URL Gambar */}
                <div>
                  <label className="text-xs text-gray-400 uppercase font-semibold mb-1 block">URL Gambar Banner (Opsional)</label>
                  <div className="flex gap-2">
                    <input 
                      name="imageUrl" 
                      onChange={handleChange} 
                      className="w-full bg-gray-800 border border-gray-700 rounded p-3 text-white focus:border-blue-500 outline-none" 
                      placeholder="https://i.imgur.com/contoh.webp" 
                    />
                    {/* Preview Kecil di Samping Input */}
                    {formData.imageUrl && (
                      <img 
                        src={formData.imageUrl} 
                        alt="Preview" 
                        className="w-12 h-12 object-cover rounded border border-gray-600"
                        onError={(e) => e.target.style.display = 'none'} // Sembunyikan jika link rusak
                      />
                    )}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">*Gunakan link gambar langsung (akhiran .jpg/.png/.webp) agar aplikasi tetap ringan.</p>
                </div>
                {/* ❌ AKHIR TAMBAHAN */}

                <div>
                  <label className="text-xs text-gray-400 uppercase font-semibold mb-1 block">Deskripsi</label>
                  <textarea 
                    name="description" 
                    onChange={handleChange} 
                    className="w-full bg-gray-800 border border-gray-700 rounded p-3 text-white focus:border-blue-500 outline-none" 
                    rows="2"
                    placeholder="Jelaskan detail syarat & ketentuan singkat..."
                  ></textarea>
                </div>
              </div>

              <hr className="border-gray-800" />

              {/* SECTION 2: DISKON */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-green-400 uppercase tracking-wider">Perhitungan Diskon</h4>
                {/* ✅ UBAH MENJADI grid-cols-2 md:grid-cols-4 */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-800/50 p-4 rounded-xl border border-gray-700/50">
                  <div>
                    <label className="text-xs text-gray-400 font-semibold mb-1 block">Diskon (%)</label>
                    <div className="relative">
                      <input type="number" name="discountPercent" required onChange={handleChange} className="w-full bg-gray-900 border border-gray-600 rounded p-2 pl-3 text-white focus:border-green-500 outline-none" placeholder="50" />
                      <span className="absolute right-3 top-2 text-gray-500 text-sm">%</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-semibold mb-1 block">Max Potongan</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500 text-xs">Rp</span>
                      <input type="number" name="maxDiscount" required onChange={handleChange} className="w-full bg-gray-900 border border-gray-600 rounded p-2 pl-8 text-white focus:border-green-500 outline-none" placeholder="20000" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-semibold mb-1 block">Min. Belanja</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500 text-xs">Rp</span>
                      <input type="number" name="minTransaction" onChange={handleChange} className="w-full bg-gray-900 border border-gray-600 rounded p-2 pl-8 text-white focus:border-green-500 outline-none" placeholder="50000" />
                    </div>
                  </div>
                  {/* ✅ TAMBAHAN INPUT JARAK */}
                  <div>
                    <label className="text-xs text-gray-400 font-semibold mb-1 block">Min. Jarak (Transport)</label>
                    <div className="relative">
                      <input type="number" name="minDistance" onChange={handleChange} className="w-full bg-gray-900 border border-gray-600 rounded p-2 pl-3 text-white focus:border-green-500 outline-none" placeholder="0" />
                      <span className="absolute right-3 top-2 text-gray-500 text-sm">KM</span>
                    </div>
                  </div>
                </div>
              </div>

              <hr className="border-gray-800" />

              {/* ✅ TAMBAHAN BARU: SECTION TARGET & KUOTA */}
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-pink-400 uppercase tracking-wider">Target Audiens & Kuota</h4>
                <div className="grid grid-cols-3 gap-4 bg-gray-800/50 p-4 rounded-xl border border-gray-700/50">
                  <div>
                    <label className="text-xs text-gray-400 font-semibold mb-1 block">Target Pengguna</label>
                    <select name="userSegment" value={formData.userSegment} onChange={handleChange} className="w-full bg-gray-900 border border-gray-600 rounded p-2 text-white focus:border-pink-500 outline-none text-sm">
                      <option value="all">Semua Pengguna</option>
                      <option value="new_user">Pengguna Baru</option>
                      <option value="loyal_user">Pengguna Aktif (Loyal)</option>
                      <option value="sleeping_user">Pengguna Pasif (Sleeping)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-semibold mb-1 block">Limit Pakai / Akun</label>
                    <input type="number" name="usageLimitPerUser" min="1" value={formData.usageLimitPerUser} onChange={handleChange} className="w-full bg-gray-900 border border-gray-600 rounded p-2 text-white focus:border-pink-500 outline-none text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-semibold mb-1 block">Kuota Total (0=Unlimited)</label>
                    <input type="number" name="globalQuota" min="0" value={formData.globalQuota} onChange={handleChange} className="w-full bg-gray-900 border border-gray-600 rounded p-2 text-white focus:border-pink-500 outline-none text-sm" />
                  </div>
                </div>
                
                {/* PILIH AREA KOTA */}
                <div>
                  <label className="text-xs text-gray-400 uppercase font-semibold mb-2 block">Area Berlaku <span className="lowercase font-normal text-gray-500">(Kosongkan jika berlaku Nasional)</span></label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-gray-800/30 p-3 rounded-lg border border-gray-800 max-h-40 overflow-y-auto">
                    {AVAILABLE_AREAS.map(area => (
                      <label key={area} className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer hover:bg-gray-700 p-1.5 rounded transition">
                        <input 
                          type="checkbox" 
                          checked={formData.targetAreas.includes(area)}
                          onChange={() => handleArrayChange('targetAreas', area)} 
                          className="w-3.5 h-3.5 rounded bg-gray-700 border-gray-600 text-pink-600 focus:ring-0" 
                        /> 
                        {area}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <hr className="border-gray-800" />

              {/* SECTION 4: SYARAT & KETENTUAN (Berubah penomoran dari Section 3) */}
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <label className="text-xs uppercase font-bold mb-3 block text-yellow-500">Layanan (Pilih)</label>
                  
                  <div className="space-y-2 bg-gray-800/30 p-3 rounded-lg border border-gray-800">
                    {['ZiMotor', 'ZiMobil', 'ZiMassage', 'ZiCleaning', 'ZiMove'].map(svc => (
                      <label key={svc} className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer hover:bg-gray-800 p-1 rounded transition">
                        <input type="checkbox" onChange={() => handleArrayChange('validServices', svc)} className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-green-600 focus:ring-0" /> 
                        {svc}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase font-bold mb-3 block text-purple-500">Metode Bayar</label>
                  
                  <div className="space-y-2 bg-gray-800/30 p-3 rounded-lg border border-gray-800">
                    <label className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer hover:bg-gray-800 p-1 rounded transition">
                      <input type="checkbox" onChange={() => handleArrayChange('validPaymentMethods', 0)} className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-purple-600 focus:ring-0" /> 
                      Tunai
                    </label>
                    <label className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer hover:bg-gray-800 p-1 rounded transition">
                      <input type="checkbox" onChange={() => handleArrayChange('validPaymentMethods', 1)} className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-purple-600 focus:ring-0" /> 
                      Saldo (Wallet)
                    </label>
                  </div>
                </div>
              </div>

              {/* SECTION 4: PERIODE & HAPPY HOUR */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 uppercase font-bold mb-1 block">Mulai Tanggal</label>
                  <input type="datetime-local" name="startDate" required onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-white focus:border-blue-500" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase font-bold mb-1 block">Selesai Tanggal</label>
                  <input type="datetime-local" name="endDate" required onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-white focus:border-blue-500" />
                </div>
                
                {/* ✅ TAMBAHAN INPUT WAKTU / JAM */}
                <div>
                  <label className="text-xs text-gray-400 uppercase font-bold mb-1 block">Berlaku Dari Jam</label>
                  <input type="time" name="validTimeStart" value={formData.validTimeStart} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-white focus:border-blue-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase font-bold mb-1 block">Sampai Jam</label>
                  <input type="time" name="validTimeEnd" value={formData.validTimeEnd} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-white focus:border-blue-500 outline-none" />
                </div>
              </div>
              
              <div className="flex gap-3 pt-6 border-t border-gray-800 mt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition font-medium">Batal</button>
                <button type="submit" disabled={submitting} className="flex-1 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition shadow-lg shadow-green-900/20">
                  {submitting ? 'Menyimpan...' : 'SIMPAN PROMO'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}